/**
 * Popup template for DC 311 service-request pins (source.type "dc311").
 *
 * dc311 observations carry a REAL observed_at (the 311 request's
 * ADDDATE) — unlike OLM's snapshot-only timestamps — so the popup shows
 * a true relative date. Repeat requests at the same normalized address
 * are deduped at ingest into ONE feature with `_display.repeat_count`;
 * when >1 the popup leads with the chronic-hotspot line instead.
 *
 * Feature contract (both ingest + frontend conform):
 *   id "dc311-{SERVICEREQUESTID}"
 *   properties.observed_at   — ADDDATE as ISO (real temporal truth)
 *   properties.source        — { type:"dc311", name:"DC 311 Service
 *                               Requests", license:"CC-BY-4.0",
 *                               authority_tier:3, url:<dataset landing> }
 *   properties._display      — { title:"{desc} · DC 311", address,
 *                               repeat_count, service_code, ward,
 *                               status, query_url }
 *   properties.context       — { huc12, drains_to_intake } (stamped by
 *                               the backend loader; may be null/absent)
 *
 * Kept out of explore.tsx so the copy is unit-testable (vitest) without
 * Leaflet.
 */

/**
 * Provenance chip, dark-popup edition: raised surface, 1px border in the
 * chip's own ink, text in that ink. Same construction as obs-popup.ts's
 * chipStyle() — duplicated rather than imported because obs-popup imports
 * escapeHtml FROM this module and the reverse edge would be a cycle.
 *
 * This replaces `background:#eee; color:#555`, which was a light-popup
 * chip painted onto a dark surface — `color: inherit` in the stylesheet
 * could never have rescued it, because a background is not text.
 */
const CHIP_MUTED =
  'background:var(--tt-popup-raised);color:var(--tt-popup-ink-muted);' +
  'border:1px solid var(--tt-popup-ink-muted);font-size:10px;padding:2px 6px;border-radius:3px;';

export function escapeHtml(s: unknown): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * True relative date from a real ISO timestamp. Same thresholds as the
 * generic pin popup's age string in explore.tsx.
 */
export function formatRelativeDate(iso: unknown, nowMs: number = Date.now()): string {
  const t = typeof iso === 'string' && iso ? new Date(iso).getTime() : NaN;
  if (!Number.isFinite(t)) return 'date unknown';
  const ageDays = Math.floor((nowMs - t) / 86_400_000);
  if (ageDays <= 0) return 'today';
  if (ageDays === 1) return 'yesterday';
  if (ageDays < 30) return `${ageDays} days ago`;
  if (ageDays < 365) return `${Math.floor(ageDays / 30)} mo ago`;
  return `${Math.floor(ageDays / 365)} yr ago`;
}

/**
 * Minimal structural shape. Deliberately permissive (fields narrowed at
 * runtime) — the caller hands us whatever the observations GeoJSON
 * carries, and the static TrailObservationFeature type predates the
 * dc311 source shape (`source.url`, `_display.repeat_count`).
 */
interface Dc311FeatureLike {
  id?: unknown;
  properties?: Record<string, unknown> | null;
}

interface Dc311Props {
  observed_at?: unknown;
  source?: { url?: unknown } | null;
  context?: { drains_to_intake?: unknown } | null;
  _display?: {
    title?: unknown;
    address?: unknown;
    repeat_count?: unknown;
    query_url?: unknown;
  } | null;
}

/**
 * Build the dc311 pin popup HTML.
 *
 *   {title}                                        _display.title
 *   Reported {n}× at this address in the past year (repeat_count > 1)
 *     — or — Reported {relative date}              (repeat_count ≤ 1)
 *   📍 {address}
 *   government report · DC 311
 *   view dataset ↗                                 source.url
 *   This spot drains toward: {intake}              (hidden when null)
 *
 * `wsBadgeHtml` is the pre-built watershed badge from the marker loop
 * (already escaped there); everything else is escaped here.
 */
export function buildDc311PopupHtml(
  feature: Dc311FeatureLike,
  opts: { wsBadgeHtml?: string; nowMs?: number } = {},
): string {
  const p = (feature.properties ?? {}) as Dc311Props;
  const d = p._display ?? {};
  const title = typeof d.title === 'string' && d.title ? d.title : 'DC 311 service request';
  const repeat = typeof d.repeat_count === 'number' ? d.repeat_count : 1;
  const reportedLine =
    repeat > 1
      ? `Reported ${repeat}× at this address in the past year`
      : `Reported ${formatRelativeDate(p.observed_at, opts.nowMs)}`;
  const address = typeof d.address === 'string' && d.address ? d.address : null;
  const datasetUrl =
    typeof p.source?.url === 'string' && p.source.url
      ? p.source.url
      : typeof d.query_url === 'string' && d.query_url
        ? d.query_url
        : null;
  const intake = p.context?.drains_to_intake;
  const drainsRow =
    typeof intake === 'string' && intake
      ? `<div style="margin-top: 6px; color: var(--tt-popup-ink-muted); font-size: 11px;">This spot drains toward: ${escapeHtml(intake)}</div>`
      : '';
  return `
    <div style="font: 12px/1.45 system-ui, sans-serif; color: var(--tt-popup-ink); max-width: 280px;">
      <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;">
        <span style="${CHIP_MUTED}">DC 311 report</span>
        ${opts.wsBadgeHtml ?? ''}
      </div>
      <div style="margin-top: 6px; font-weight: 600;">${escapeHtml(title)}</div>
      <div style="margin-top: 4px; color: var(--tt-popup-ink);">${escapeHtml(reportedLine)}</div>
      ${address ? `<div style="margin-top: 4px; color: var(--tt-popup-ink-muted); font-size: 11px;">📍 ${escapeHtml(address)}</div>` : ''}
      <div style="margin-top: 6px; color: var(--tt-popup-ink-muted);">government report · DC 311</div>
      ${datasetUrl ? `<div style="margin-top: 4px;"><a href="${escapeHtml(datasetUrl)}" target="_blank" rel="noopener" style="color: var(--tt-popup-link); text-decoration: underline;">view dataset ↗</a></div>` : ''}
      ${drainsRow}
    </div>
  `;
}
